/**
 * Tenant slug resolution: subdomain → tenant.
 *
 * Production: each jurisdiction gets a subdomain (oakland.platform.example).
 * The tenant is derived from window.location.hostname at runtime — no
 * build-time tenant config.
 */

export interface TenantResolution {
  /** Resolved slug, e.g. "oakland". */
  slug: string;
  /** True when VITE_TENANT_OVERRIDE supplied the slug (dev convenience). */
  fromOverride: boolean;
}

/**
 * Strip the port from a hostname, e.g. "oakland.localhost:5173" → "oakland.localhost".
 */
function stripPort(host: string): string {
  const colon = host.lastIndexOf(':');
  // Guard against IPv6 literals like "[::1]:5173".
  if (host.startsWith('[')) return host;
  return colon === -1 ? host : host.slice(0, colon);
}

function isIpAddress(host: string): boolean {
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.includes(':');
}

/**
 * Resolve the tenant slug from a hostname.
 *
 * Rules:
 *  - `oakland.platform.example`      → "oakland"
 *  - `platform.example`              → "platform" (root domain tenant; the
 *    backend decides whether this is a real tenant or unknown)
 *  - `localhost` / bare IP / single label → error (needs VITE_TENANT_OVERRIDE)
 *
 * Pure function of the hostname string → unit-testable, no window access.
 */
export function resolveTenantSlug(hostname: string): TenantResolution {
  const override = (import.meta.env.VITE_TENANT_OVERRIDE ?? '').trim();
  if (override) {
    return { slug: override, fromOverride: true };
  }

  const host = stripPort(hostname.trim().toLowerCase());
  if (!host || host === 'localhost' || isIpAddress(host)) {
    throw new Error(
      'Could not determine the tenant from this hostname. ' +
        'Set VITE_TENANT_OVERRIDE to a tenant slug when developing on localhost.',
    );
  }

  const parts = host.split('.').filter((p) => p.length > 0);
  const first = parts[0];
  if (parts.length >= 3 && first !== undefined) {
    return { slug: first, fromOverride: false };
  }
  if (parts.length === 2 && first !== undefined) {
    // Two-label host (e.g. platform.example in dev) → first label as slug;
    // the backend returns 404 for unknown slugs, which we surface cleanly.
    return { slug: first, fromOverride: false };
  }
  throw new Error(`Unrecognized hostname "${hostname}". Set VITE_TENANT_OVERRIDE to continue.`);
}

/** Convenience wrapper reading the live browser hostname. */
export function resolveTenantSlugFromWindow(): TenantResolution {
  return resolveTenantSlug(window.location.hostname);
}
